import { useState, useEffect, useCallback } from 'react'
import {
  Users, Plus, Trash2, RefreshCw, Copy, Key, Shield, CheckCircle,
  Download, Github, Activity, ExternalLink, Server, Cloud, XCircle,
  Cpu, Zap, AlertCircle,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface SourceInfo {
  repo: string
  branch: string
  public_url: string
  archive_url: string
  clone_url_https: string
  clone_url_ssh: string
  description?: string
  stars?: number
  size_kb?: number
  updated_at?: string
  visibility?: string
  license?: string
  note?: string
  latest_commit?: {
    sha: string
    message: string
    author: string
    date: string
  }
  latest_release?: {
    tag: string
    name: string
    published_at: string
    html_url: string
    zipball_url: string
    tarball_url: string
  } | null
  self_host_steps: string[]
  stack: Record<string, string>
  fetched_at: string
}

export default function AdminPage() {
  const [users, setUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  // First admin (bootstrap account) — we don't let the UI delete it
  const [firstAdminEmail, setFirstAdminEmail] = useState<string>('')
  // Public origin for copy-paste snippets (OAuth callback URLs, etc.)
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const host = typeof window !== 'undefined' ? window.location.host : 'example.com'
  const domain = host.includes('.') ? host.split('.').slice(-2).join('.') : host
  const [showCreate, setShowCreate] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newRole, setNewRole] = useState('viewer')
  const [createResult, setCreateResult] = useState<any>(null)
  const [resetResult, setResetResult] = useState<any>(null)
  const [source, setSource] = useState<SourceInfo | null>(null)
  const [sourceLoading, setSourceLoading] = useState(false)
  const [metricsPreview, setMetricsPreview] = useState<string>('')
  const [metricsLoading, setMetricsLoading] = useState(false)
  const [ssoConfig, setSsoConfig] = useState<{ enabled: boolean; tenant_id?: string | null; client_id?: string | null; allowed_domains: string[]; redirect_uri: string; auto_provision: boolean } | null>(null)
  // ── AI Models registry ──────────────────────────────────────────────────
  const [models, setModels] = useState<any>(null)
  const [modelsLoading, setModelsLoading] = useState(false)
  const [canaryResults, setCanaryResults] = useState<Record<string, any>>({})
  const [adoptBusy, setAdoptBusy] = useState<string>('')

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/admin/users')
      const rows = data.users || []
      setUsers(rows)
      // Preserve the very first admin account — sort by created_at ASC, first admin wins
      const firstAdmin = [...rows]
        .filter((u: any) => u.role === 'admin')
        .sort((a: any, b: any) => String(a.created_at || '').localeCompare(String(b.created_at || '')))[0]
      if (firstAdmin?.email) setFirstAdminEmail(firstAdmin.email)
    } finally { setLoading(false) }
  }, [])

  const fetchSource = useCallback(async () => {
    setSourceLoading(true)
    try {
      const { data } = await api.get('/api/source/info')
      setSource(data)
    } finally { setSourceLoading(false) }
  }, [])

  const fetchMetricsPreview = useCallback(async () => {
    setMetricsLoading(true)
    try {
      const { data } = await api.get('/api/metrics', { responseType: 'text' })
      setMetricsPreview(typeof data === 'string' ? data : String(data))
    } finally { setMetricsLoading(false) }
  }, [])

  const fetchSSO = useCallback(async () => {
    try {
      const { data } = await api.get('/api/auth/microsoft/config')
      setSsoConfig(data)
    } catch { /* ignore */ }
  }, [])

  const fetchModels = useCallback(async () => {
    setModelsLoading(true)
    try {
      const { data } = await api.get('/api/admin/models/registry')
      setModels(data)
    } catch { /* admin-only / unreachable — section stays quiet */ }
    finally { setModelsLoading(false) }
  }, [])

  const runCanary = async (provider: string, model_id: string) => {
    const key = `${provider}:${model_id}`
    setAdoptBusy(key)
    try {
      const { data } = await api.post('/api/admin/models/canary', { provider, model_id })
      setCanaryResults(prev => ({ ...prev, [key]: data }))
    } catch (e: any) {
      setCanaryResults(prev => ({ ...prev, [key]: { passed: false, reason: e?.message || 'error' } }))
    } finally { setAdoptBusy('') }
  }

  const adoptModel = async (provider: string, model_id: string) => {
    const key = `${provider}:${model_id}`
    setAdoptBusy(key)
    try {
      const { data } = await api.post('/api/admin/models/adopt', { provider, model_id })
      setCanaryResults(prev => ({ ...prev, [key]: data.canary }))
      await fetchModels()
    } catch (e: any) {
      setCanaryResults(prev => ({ ...prev, [key]: { passed: false, reason: e?.message || 'error' } }))
    } finally { setAdoptBusy('') }
  }

  const setAutoAdopt = async (enabled: boolean) => {
    try {
      await api.post('/api/admin/models/auto-adopt', { enabled })
      await fetchModels()
    } catch { /* ignore */ }
  }

  useEffect(() => { fetchUsers(); fetchSource(); fetchSSO(); fetchModels() }, [fetchUsers, fetchSource, fetchSSO, fetchModels])

  const createUser = async () => {
    if (!newEmail || !newPassword) return
    try {
      const { data } = await api.post('/api/admin/users', { email: newEmail, password: newPassword, role: newRole })
      setCreateResult(data)
      setNewEmail('')
      setNewPassword('')
      fetchUsers()
    } catch (err: any) {
      setCreateResult({ error: err.response?.data?.detail || 'Failed to create user' })
    }
  }

  const deleteUser = async (email: string) => {
    if (!confirm(`Delete user ${email}?`)) return
    try {
      await api.delete(`/api/admin/users/${encodeURIComponent(email)}`)
      fetchUsers()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete user')
    }
  }

  const resetMFA = async (email: string) => {
    try {
      const { data } = await api.put(`/api/admin/users/${encodeURIComponent(email)}/reset-mfa`)
      setResetResult(data)
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to reset MFA')
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  return (
    <div className="space-y-4 animate-fade-in max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Users size={20} className="text-blue" /> Admin — User Management
          </h1>
          <p className="text-[10px] text-text-secondary">{users.length} user(s) — create accounts with MFA for your team</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => { setShowCreate(!showCreate); setCreateResult(null) }} className="btn-primary text-xs">
            <Plus size={12} /> New User
          </button>
          <button type="button" onClick={fetchUsers} className="btn-secondary text-xs" title="Refresh users" aria-label="Refresh users">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Create user form */}
      {showCreate && (
        <div className="card p-5 border border-blue/20">
          <h2 className="text-sm font-semibold text-text-primary mb-3">Create New User</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-text-muted block mb-1">Email</label>
              <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="user@company.com" className="input py-2 text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-text-muted block mb-1">Password</label>
              <input value={newPassword} onChange={e => setNewPassword(e.target.value)} type="password" placeholder="Strong password" className="input py-2 text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-text-muted block mb-1">Role</label>
              <select title="Role" value={newRole} onChange={e => setNewRole(e.target.value)} className="input py-2 text-xs">
                <option value="admin">Admin</option>
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
          </div>
          <button type="button" onClick={createUser} className="btn-primary text-xs mt-3">
            <Plus size={12} /> Create User
          </button>

          {/* Create result with MFA setup info */}
          {createResult && !createResult.error && (
            <div className="mt-4 p-4 rounded-lg border border-emerald/30 bg-emerald/5">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={16} className="text-emerald" />
                <span className="text-sm font-semibold text-emerald">User Created Successfully</span>
              </div>
              <div className="space-y-2 text-xs">
                <p className="text-text-primary"><b>Email:</b> {createResult.email}</p>
                <p className="text-text-primary"><b>Role:</b> {createResult.role}</p>

                <div className="p-3 rounded bg-surface-card border border-border">
                  <p className="text-[10px] text-text-muted uppercase tracking-wider font-semibold mb-1">MFA Setup — Share this with the user</p>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] text-text-muted">TOTP Secret:</span>
                    <code className="font-mono text-amber-light bg-surface-hover px-2 py-0.5 rounded text-xs">{createResult.totp_secret}</code>
                    <button type="button" onClick={() => copyToClipboard(createResult.totp_secret)} className="btn-ghost p-1" title="Copy">
                      <Copy size={12} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-text-muted">Provisioning URI:</span>
                    <code className="font-mono text-blue-light bg-surface-hover px-2 py-0.5 rounded text-[9px] break-all">{createResult.provisioning_uri}</code>
                    <button type="button" onClick={() => copyToClipboard(createResult.provisioning_uri)} className="btn-ghost p-1" title="Copy">
                      <Copy size={12} />
                    </button>
                  </div>
                </div>
                <p className="text-[10px] text-text-secondary">{createResult.setup_instructions}</p>
              </div>
            </div>
          )}

          {createResult?.error && (
            <div className="mt-3 p-3 rounded border border-rose/30 bg-rose/5 text-rose-light text-xs">{createResult.error}</div>
          )}
        </div>
      )}

      {/* MFA Reset result */}
      {resetResult && (
        <div className="card p-4 border border-amber/30 bg-amber/5">
          <div className="flex items-center gap-2 mb-2">
            <Key size={16} className="text-amber" />
            <span className="text-sm font-semibold text-amber-light">MFA Reset — {resetResult.email}</span>
            <button type="button" onClick={() => setResetResult(null)} className="ml-auto text-text-muted hover:text-text-primary text-xs">Dismiss</button>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] text-text-muted">New TOTP Secret:</span>
            <code className="font-mono text-amber-light bg-surface-hover px-2 py-0.5 rounded text-xs">{resetResult.totp_secret}</code>
            <button type="button" onClick={() => copyToClipboard(resetResult.totp_secret)} className="btn-ghost p-1" title="Copy"><Copy size={12} /></button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-text-muted">URI:</span>
            <code className="font-mono text-blue-light bg-surface-hover px-2 py-0.5 rounded text-[9px] break-all">{resetResult.provisioning_uri}</code>
            <button type="button" onClick={() => copyToClipboard(resetResult.provisioning_uri)} className="btn-ghost p-1" title="Copy"><Copy size={12} /></button>
          </div>
          <p className="text-[10px] text-text-secondary mt-1">{resetResult.setup_instructions}</p>
        </div>
      )}

      {/* Users table */}
      <div className="card overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-text-muted border-b border-border bg-surface-card">
              <th className="py-2.5 px-4">Email</th>
              <th className="py-2.5 px-4">Role</th>
              <th className="py-2.5 px-4">MFA</th>
              <th className="py-2.5 px-4">Created</th>
              <th className="py-2.5 px-4">Last Login</th>
              <th className="py-2.5 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.email} className="border-b border-border hover:bg-surface-hover">
                <td className="py-2.5 px-4 font-medium text-text-primary">{user.email}</td>
                <td className="py-2.5 px-4">
                  <span className={clsx('badge text-[9px]',
                    user.role === 'admin' ? 'badge-purple' : user.role === 'editor' ? 'badge-blue' : 'badge-green')}>
                    {user.role}
                  </span>
                </td>
                <td className="py-2.5 px-4">
                  {user.mfa_enabled ? <span className="text-emerald">Enabled</span> : <span className="text-text-muted">Disabled</span>}
                </td>
                <td className="py-2.5 px-4 text-text-muted">{user.created_at?.slice(0, 10)}</td>
                <td className="py-2.5 px-4 text-text-muted">{user.last_login === 'Never' ? 'Never' : user.last_login?.slice(0, 16)}</td>
                <td className="py-2.5 px-4">
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => resetMFA(user.email)} className="btn-ghost p-1 text-amber-light" title="Reset MFA">
                      <Key size={13} />
                    </button>
                    {/* Don't allow deletion of the first-admin bootstrap account */}
                    {user.email !== firstAdminEmail && (
                      <button type="button" onClick={() => deleteUser(user.email)} className="btn-ghost p-1 text-rose-light" title="Delete user">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <p className="text-xs text-text-muted text-center py-6">No users</p>}
      </div>

      {/* ── Microsoft Entra SSO ─────────────────────────────────────────── */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <Cloud size={16} className="text-blue" /> Microsoft Entra ID (Azure AD) SSO
          </h2>
          {ssoConfig ? (
            ssoConfig.enabled ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border border-emerald/30 bg-emerald/10 text-emerald">
                <CheckCircle size={10} /> Enabled
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border border-text-muted/20 bg-surface-raised text-text-muted">
                <XCircle size={10} /> Disabled
              </span>
            )
          ) : null}
        </div>

        {ssoConfig?.enabled ? (
          <div className="space-y-2 text-xs">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[9px] uppercase tracking-wider text-text-muted">Tenant</div>
                <code className="font-mono text-text-primary text-[11px]">{ssoConfig.tenant_id}</code>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wider text-text-muted">Client ID</div>
                <code className="font-mono text-text-primary text-[11px]">{ssoConfig.client_id}</code>
              </div>
              <div className="col-span-2">
                <div className="text-[9px] uppercase tracking-wider text-text-muted">Redirect URI</div>
                <code className="font-mono text-text-primary text-[11px] break-all">{ssoConfig.redirect_uri}</code>
              </div>
            </div>
            <div className="text-[11px] text-text-secondary">
              Auto-provisioning:{' '}
              {ssoConfig.auto_provision ? (
                <>enabled for <b>{ssoConfig.allowed_domains.join(', ')}</b> domain(s) — new users from these domains are created with <code>viewer</code> role on first sign-in.</>
              ) : (
                <>disabled — admins must pre-create user records before first sign-in.</>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-xs">
            <p className="text-text-secondary">
              Sign-in with Microsoft is <b>off</b>. Enable it by registering an application in Entra ID and
              setting the env vars on the server.
            </p>

            <div className="rounded border border-border p-3 bg-surface-raised/50">
              <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-2">
                Entra app registration checklist
              </div>
              <ol className="space-y-1.5 list-decimal list-inside text-[11px] text-text-secondary">
                <li>
                  Entra admin centre → <b>Applications → App registrations → New registration</b>.
                  Name: whatever you like (e.g. the instance name from your <code>.env</code>).
                </li>
                <li>
                  Supported account types: typically <b>Single tenant</b> (only your directory).
                </li>
                <li>
                  Redirect URI: <b>Web</b> →{' '}
                  <code className="font-mono text-blue-light break-all">
                    {origin}/api/auth/microsoft/callback
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(`${origin}/api/auth/microsoft/callback`)}
                    className="btn-ghost p-1 inline-flex" title="Copy redirect URI"
                  >
                    <Copy size={10} />
                  </button>
                </li>
                <li>
                  After creation, note the <b>Application (client) ID</b> and <b>Directory (tenant) ID</b>.
                </li>
                <li>
                  <b>Certificates &amp; secrets → New client secret</b>. Copy the secret value (shown once).
                </li>
                <li>
                  <b>API permissions</b>: Microsoft Graph → Delegated →{' '}
                  <code>openid</code>, <code>email</code>, <code>profile</code>, <code>offline_access</code>,{' '}
                  <code>User.Read</code>. Grant admin consent.
                </li>
                <li>On the server (<code className="font-mono">backend/.env</code>) add:</li>
              </ol>
              <pre className="text-[10px] font-mono bg-base border border-border rounded p-2 mt-2 overflow-x-auto">
{`MS_TENANT_ID=<Directory-tenant-GUID>
MS_CLIENT_ID=<Application-client-GUID>
MS_CLIENT_SECRET=<the-secret-value>
MS_REDIRECT_URI=${origin}/api/auth/microsoft/callback
MS_ALLOWED_DOMAINS=${domain}
FRONTEND_URL=${origin}`}
              </pre>
              <button
                type="button"
                onClick={() => copyToClipboard(`MS_TENANT_ID=\nMS_CLIENT_ID=\nMS_CLIENT_SECRET=\nMS_REDIRECT_URI=${origin}/api/auth/microsoft/callback\nMS_ALLOWED_DOMAINS=${domain}\nFRONTEND_URL=${origin}\n`)}
                className="btn-ghost text-[10px] mt-1"
              >
                <Copy size={10} /> Copy env block
              </button>
              <p className="text-[10px] text-text-muted mt-2">
                Then <code>docker restart cto-api</code>. The "Sign in with Microsoft" button will appear on the login page.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Source Code / Self-Host ─────────────────────────────────────── */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <Github size={16} className="text-text-secondary" /> Source Code — Bring It In-House
          </h2>
          <button type="button" onClick={fetchSource} className="btn-secondary text-xs" title="Refresh source info" aria-label="Refresh source info">
            <RefreshCw size={12} className={sourceLoading ? 'animate-spin' : ''} />
          </button>
        </div>

        {!source ? (
          <p className="text-xs text-text-muted">Loading repository metadata…</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div>
                <div className="text-[9px] uppercase tracking-wider text-text-muted">Repository</div>
                <a href={source.public_url} target="_blank" rel="noreferrer noopener" className="text-blue-light font-mono flex items-center gap-1 hover:underline">
                  {source.repo} <ExternalLink size={10} />
                </a>
                {source.visibility && (
                  <span className="text-[10px] text-text-muted">
                    {source.visibility} · branch <code className="font-mono">{source.branch}</code>
                    {source.license && <> · {source.license}</>}
                  </span>
                )}
              </div>
              {source.latest_commit && (
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-text-muted">Latest commit</div>
                  <code className="font-mono text-xs text-amber-light">{source.latest_commit.sha}</code>
                  <div className="text-[10px] text-text-secondary truncate max-w-xs" title={source.latest_commit.message}>
                    {source.latest_commit.message}
                  </div>
                  <div className="text-[10px] text-text-muted">
                    {source.latest_commit.author} · {source.latest_commit.date?.slice(0, 10)}
                  </div>
                </div>
              )}
              <div>
                <div className="text-[9px] uppercase tracking-wider text-text-muted">Size / Stars</div>
                <div className="text-xs text-text-primary font-mono">
                  {source.size_kb ? `${(source.size_kb / 1024).toFixed(1)} MB` : '—'}
                  {source.stars != null && <> · ★ {source.stars}</>}
                </div>
                {source.updated_at && (
                  <div className="text-[10px] text-text-muted">
                    pushed {source.updated_at.slice(0, 10)}
                  </div>
                )}
              </div>
            </div>

            {source.note && (
              <div className="text-[10px] text-amber-light bg-amber/5 border border-amber/20 rounded px-2 py-1.5">
                {source.note}
              </div>
            )}

            {source.latest_release && (
              <div className="p-2 rounded border border-emerald/30 bg-emerald/5 text-[11px]">
                <div className="flex items-center gap-2">
                  <CheckCircle size={12} className="text-emerald" />
                  <span className="text-emerald font-semibold">Latest release: {source.latest_release.tag}</span>
                  <span className="text-text-muted">
                    · published {source.latest_release.published_at?.slice(0, 10)}
                  </span>
                  <a
                    href={source.latest_release.html_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-blue-light hover:underline ml-auto"
                  >
                    View on GitHub
                  </a>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <a
                href="/api/source/download"
                className="btn-primary text-xs"
                download
                title="Always serves the current HEAD of the main branch"
              >
                <Download size={12} /> Download .zip (latest HEAD)
              </a>
              {source.latest_release && (
                <a
                  href={source.latest_release.zipball_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn-secondary text-xs"
                  title="Tagged release snapshot"
                >
                  <Download size={12} /> Release {source.latest_release.tag}
                </a>
              )}
              <button
                type="button"
                onClick={() => copyToClipboard(source.clone_url_https)}
                className="btn-secondary text-xs"
                title="Copy HTTPS clone URL"
              >
                <Copy size={12} /> git clone (HTTPS)
              </button>
              <button
                type="button"
                onClick={() => copyToClipboard(source.clone_url_ssh)}
                className="btn-secondary text-xs"
                title="Copy SSH clone URL"
              >
                <Copy size={12} /> git clone (SSH)
              </button>
              <a
                href={source.archive_url}
                target="_blank"
                rel="noreferrer noopener"
                className="btn-ghost text-xs"
              >
                <ExternalLink size={12} /> Direct GitHub archive
              </a>
            </div>

            <details className="text-xs text-text-secondary">
              <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-text-muted hover:text-text-secondary">
                Self-host steps · Stack
              </summary>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 p-3 rounded bg-surface-raised/50">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
                    Setup
                  </div>
                  <ol className="space-y-1 list-none">
                    {source.self_host_steps.map((s, i) => (
                      <li key={i} className="text-[11px] text-text-secondary">{s}</li>
                    ))}
                  </ol>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
                    Stack
                  </div>
                  <ul className="space-y-1">
                    {Object.entries(source.stack).map(([k, v]) => (
                      <li key={k} className="text-[11px] text-text-secondary">
                        <span className="font-semibold text-text-primary capitalize">{k}:</span> {v}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </details>
          </div>
        )}
      </div>

      {/* ── Grafana / Prometheus Metrics ────────────────────────────────── */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <Activity size={16} className="text-orange" /> Grafana Plumbing
          </h2>
          <div className="flex gap-2">
            <button type="button" onClick={fetchMetricsPreview} className="btn-secondary text-xs">
              <RefreshCw size={12} className={metricsLoading ? 'animate-spin' : ''} /> Preview /api/metrics
            </button>
          </div>
        </div>

        <p className="text-xs text-text-secondary mb-3">
          Prometheus-format metrics are exposed at{' '}
          <code className="font-mono text-blue-light">/api/metrics</code>. Scrape with Prometheus,
          Grafana Alloy, or any OpenMetrics-compatible collector. Import{' '}
          <code className="font-mono">grafana/ctodashboard.json</code> from the repo for a
          pre-built dashboard.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted font-semibold mb-1">
              Scrape config
            </div>
            <pre className="text-[10px] font-mono bg-surface-raised/50 border border-border rounded p-2 overflow-x-auto">
{`scrape_configs:
  - job_name: ctodashboard
    metrics_path: /api/metrics
    scheme: https
    scrape_interval: 60s
    static_configs:
      - targets: ['${host}']`}
            </pre>
            <button
              type="button"
              onClick={() => copyToClipboard(`scrape_configs:\n  - job_name: ctodashboard\n    metrics_path: /api/metrics\n    scheme: https\n    scrape_interval: 60s\n    static_configs:\n      - targets: ['${host}']\n`)}
              className="btn-ghost text-[10px] mt-1"
            >
              <Copy size={10} /> Copy
            </button>
          </div>

          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted font-semibold mb-1">
              Available metrics
            </div>
            <ul className="text-[10px] font-mono text-text-secondary space-y-0.5">
              <li><span className="text-emerald">cto_up</span> — API alive (1/0)</li>
              <li><span className="text-emerald">cto_articles_total</span>{'{category}'} </li>
              <li><span className="text-emerald">cto_articles_recent</span>{'{category,window}'}</li>
              <li><span className="text-emerald">cto_signals_total</span>{'{severity}'}</li>
              <li><span className="text-emerald">cto_cves_total</span>{'{severity}'}</li>
              <li><span className="text-emerald">cto_cves_exploited</span> — CISA KEV count</li>
              <li><span className="text-emerald">cto_cloud_status</span>{'{provider,status}'}</li>
              <li><span className="text-emerald">cto_ingestion_lag_seconds</span></li>
              <li><span className="text-emerald">cto_db_rows_*</span> — per-table counts</li>
            </ul>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <a href="/api/metrics" target="_blank" rel="noreferrer noopener" className="btn-primary text-xs">
            <Server size={12} /> Open /api/metrics
          </a>
          <a
            href={`${source?.public_url || 'https://github.com'}/blob/main/grafana/ctodashboard.json`}
            target="_blank"
            rel="noreferrer noopener"
            className="btn-secondary text-xs"
          >
            <ExternalLink size={12} /> Dashboard JSON
          </a>
          <a
            href={`${source?.public_url || 'https://github.com'}/blob/main/grafana/README.md`}
            target="_blank"
            rel="noreferrer noopener"
            className="btn-ghost text-xs"
          >
            <ExternalLink size={12} /> Setup guide
          </a>
        </div>

        {metricsPreview && (
          <details open className="mt-3">
            <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-text-muted hover:text-text-secondary">
              Live metrics output
            </summary>
            <pre className="text-[10px] font-mono bg-surface-raised/50 border border-border rounded p-2 overflow-x-auto mt-1 max-h-64 overflow-y-auto">
              {metricsPreview}
            </pre>
          </details>
        )}
      </div>

      {/* ─── AI Models registry ─────────────────────────────────────────── */}
      <div className="card p-5 border border-border">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
              <Cpu size={16} className="text-purple" /> AI Models — current vs latest
            </h2>
            <p className="text-[10px] text-text-secondary">
              Detect newer provider models and adopt one (canary-gated) without an env edit.
              {models?.detected?.checked_at && (
                <> Last checked {new Date(models.detected.checked_at).toLocaleString()}.</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-[10px] text-text-secondary cursor-pointer" title="Auto-adopt only switches after a model passes the canary test.">
              <input
                type="checkbox"
                checked={!!models?.auto_adopt}
                onChange={e => setAutoAdopt(e.target.checked)}
              />
              Auto-adopt latest
            </label>
            <button type="button" onClick={fetchModels} className="btn-secondary text-xs" title="Check now">
              <RefreshCw size={12} className={modelsLoading ? 'animate-spin' : ''} /> Check now
            </button>
          </div>
        </div>

        <p className="text-[9px] text-text-muted mb-3">
          Auto-adopt only switches after a model passes the canary test. Default off — nothing changes without an explicit opt-in.
        </p>

        {(!models || (models.detected?.providers || []).length === 0) && !modelsLoading && (
          <p className="text-xs text-text-muted">No provider with a model-list API key reachable. Set a provider API key (e.g. DEEPSEEK_API_KEY) to detect models.</p>
        )}

        {(models?.detected?.providers || []).map((prov: any) => (
          <div key={prov.provider} className="mb-4 border border-border/60 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-text-primary capitalize">
                {prov.provider}: <span className="font-mono text-blue">{prov.current_model || 'unset'}</span>
              </span>
              {prov.update_available ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber/15 text-amber flex items-center gap-1">
                  <AlertCircle size={11} /> {prov.newer_than_current.length} newer available
                </span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald/15 text-emerald flex items-center gap-1">
                  <CheckCircle size={11} /> up to date
                </span>
              )}
            </div>

            {prov.frontier_context?.length > 0 && (
              <div className="text-[10px] text-text-secondary mb-2">
                {prov.frontier_context.slice(0, 3).map((f: any, i: number) => (
                  <div key={i}>· {f.model} — released {f.date}, tracked in Frontier</div>
                ))}
              </div>
            )}

            <div className="space-y-1 max-h-56 overflow-y-auto">
              {(prov.available || []).slice(0, 12).map((m: any) => {
                const key = `${prov.provider}:${m.id}`
                const cr = canaryResults[key]
                const isCurrent = m.id === prov.current_model
                const busy = adoptBusy === key
                return (
                  <div key={m.id} className="flex items-center justify-between gap-2 text-[11px] py-1 border-b border-border/30 last:border-0">
                    <span className="font-mono truncate">{m.id}{isCurrent && <span className="ml-1 text-emerald">(active)</span>}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      {cr && (
                        <span className={clsx('text-[10px] flex items-center gap-1', cr.passed ? 'text-emerald' : 'text-red')}>
                          {cr.passed ? <CheckCircle size={11} /> : <XCircle size={11} />}
                          canary {cr.passed ? 'pass' : 'fail'}{typeof cr.latency_ms === 'number' && ` · ${cr.latency_ms}ms`}
                        </span>
                      )}
                      {!isCurrent && (
                        <>
                          <button type="button" disabled={busy} onClick={() => runCanary(prov.provider, m.id)} className="btn-ghost text-[10px] px-2 py-0.5" title="Run canary test">
                            <Zap size={10} /> Canary
                          </button>
                          <button type="button" disabled={busy} onClick={() => adoptModel(prov.provider, m.id)} className="btn-primary text-[10px] px-2 py-0.5">
                            {busy ? '…' : 'Adopt'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}

        {models?.adopt_history?.length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-text-muted hover:text-text-secondary">Adopt history</summary>
            <div className="text-[10px] text-text-secondary mt-1 space-y-0.5">
              {models.adopt_history.map((h: any, i: number) => (
                <div key={i} className="font-mono">
                  {h.event === 'auto_adopt_policy'
                    ? `auto-adopt ${h.enabled ? 'enabled' : 'disabled'}`
                    : `${h.provider}/${h.model_id}${h.forced ? ' (FORCED)' : ''}`} — {h.by} — {h.at && new Date(h.at).toLocaleString()}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  )
}
